import loggingService from 'common/logging/loggingService';
import notificationService from 'common/notificationService';
import Loading from 'common/ui/Loading';
import useStartup from 'common/ui/useStartup';
import { OrderType } from 'domain/orderType';
import AddTraderModal from 'features/settings/traders/AddTraderModal';
import DeleteTraderModal from 'features/settings/traders/DeleteTraderModal';
import { Trader } from 'features/settings/traders/trader';
import React, { useState } from 'react';
import MerchantDetailsComponentProps from '../merchantDetailsComponentProps';
import TraderList from './TraderList';
import traderService from './traderService';

const TraderSettings: React.FC<MerchantDetailsComponentProps> = props => {
    const [traders, setTraders] = useState<Trader[]>(null);
    const [showModal, setShowModal] = useState(false);
    const [editingTrader, setEditingTrader] = useState<Trader>(null);
    const [showEditMode, setShowEditMode] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    useStartup(() => {
        traderService.listTraders(props.merchant.id)
            .then(t => setTraders(t));
    });

    function isLoaded() {
        return traders != null;
    }

    function handleCancel() {
        setShowModal(false);
    }

    function handleDeleteModalCancel() {
        setShowModal(true);
        setShowDeleteModal(false);
    }

    function showDelete() {
        setShowModal(false);
        setShowDeleteModal(true);
    }

    function onTraderSelected(trader: Trader) {
        setShowModal(true);
        setShowEditMode(true);
        setEditingTrader({ ...trader });
    };

    async function handleSaveTrader(trader: Trader): Promise<any> {
        if (showEditMode) {
            await traderService.updateTrader(props.merchant.id, trader);
        } else {
            await traderService.createTrader(props.merchant.id, trader);
        }
        traderService.listTraders(props.merchant.id)
            .then(t => setTraders(t));
        setShowModal(false);
    }

    async function handleDeleteTrader(trader: Trader): Promise<any> {
        try {
            await traderService.deleteTrader(props.merchant.id, trader);
            traderService.listTraders(props.merchant.id)
                .then(t => setTraders(t));
        }
        catch {
            notificationService.error(`Error deleting trader ${trader.email}`)
        }
        setShowDeleteModal(false);
    }

    async function onTraderUpdated(trader: Trader) {
        try {
            await traderService.updateTraderNonPro(props.merchant.id, trader);
            setTraders(m => m.map(m => m.userId === trader.userId ? trader : m))
        }
        catch (err) {
            notificationService.error("Error updating trader");
            loggingService.error("Error updating trader", err as Error);
        }
    }

    function createNewTrader() {
        const defaultTrader = { ...new Trader(), maxOfferQuantity: 45_000, orderType: OrderType.Limit };
        setEditingTrader(defaultTrader);
        setShowEditMode(false);
        setShowModal(true);
    }

    if (!isLoaded()) return <Loading />

    return <div>
        <div className="">
            <div className="mt-4">
                <div className="header">
                    <h4>Traders
                    <button className="btn" onClick={createNewTrader}>
                        <i className="material-icons">add</i>
                    </button>
                    </h4>
                </div>
            </div>
            {showModal && <AddTraderModal
                trader={editingTrader}
                showModal={showModal}
                showEditMode={showEditMode}
                handleCancel={handleCancel}
                handleSubmit={handleSaveTrader}
                showDeleteModal={showDelete}
            />}
            {showDeleteModal && <DeleteTraderModal
                trader={editingTrader}
                showDeleteModal={showDeleteModal}
                handleDeleteModalCancel={handleDeleteModalCancel}
                handleDeleteSubmit={handleDeleteTrader}
            />}
            <TraderList
                traders={traders}
                onTraderUpdated={onTraderUpdated}
                onTraderSelected={onTraderSelected}
            />
        </div>
    </div>;
}

export default TraderSettings;